import { motion } from "framer-motion";
import ReCAPTCHA from "react-google-recaptcha";
import { Link, useLocation } from "react-router-dom";

import {
  Button,
  Checkbox,
  FloatingLabelInput,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Separator,
} from "@/components";
import { config } from "@/config/config";
import { useI18n } from "@/context/i18n";

import { FacebookLogin } from "../login/components/facebook-login";
import { GoogleLogin } from "../login/components/google-login";
import { useLogin } from "../login/login.hooks";
import { useRegister } from "./register.hooks";

export function RegisterPage() {
  const { t } = useI18n();
  const {
    handleRegister,
    form,
    googleRecaptchaSiteKey,
    reCaptchaRef,
    isSimple,
  } = useRegister();
  const { onSuccessGoogle, onSuccessFacebook, facebookAppId } = useLogin();
  const location = useLocation();

  return (
    <div className="flex min-h-screen items-center justify-center p-4 bg-background">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md space-y-6 bg-background/80 p-8 shadow-lg border border-muted/20"
      >
        <div className="flex flex-col items-center space-y-2 text-center">
          <div className="w-24 h-24 bg-menu-item/5 rounded-full flex items-center justify-center">
            <img src={config.logoUrl} alt={config.brandName} className="w-18 h-18" />
          </div>
          <h1 className="text-2xl font-bold">{t("auth.register")}</h1>
          <p className="text-sm text-muted-foreground">
            {t("auth.createYourAccount")}{" "}
            <span className="text-primary font-bold">{config.brandName}</span>
          </p>
        </div>

        <div className="space-y-4">
          <GoogleLogin onSuccessGoogle={onSuccessGoogle} />
          <FacebookLogin
            onSuccess={onSuccessFacebook}
            appClientId={facebookAppId}
          />
        </div>

        <div className="flex items-center">
          <Separator className="flex-1" />
          <span className="px-3 text-xs text-muted-foreground">
            {t("auth.registerWithEmail")}
          </span>
          <Separator className="flex-1" />
        </div>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleRegister)}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="fullName"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <FloatingLabelInput
                      label={t("auth.fullName")}
                      placeholder={t("auth.fullName")}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <FloatingLabelInput
                      label={t("auth.email")}
                      type="email"
                      placeholder={t("auth.email")}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {!isSimple && (
              <>
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <FloatingLabelInput
                          label={t("auth.password")}
                          type="password"
                          placeholder="••••••••"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <FloatingLabelInput
                          label={t("auth.confirmPassword")}
                          type="password"
                          placeholder="••••••••"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            )}

            <FormField
              control={form.control}
              name="acceptTerms"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="leading-none flex flex-col gap-4">
                    <FormLabel>{t("auth.terms")}</FormLabel>
                    <FormMessage className="-ml-6" />
                  </div>
                </FormItem>
              )}
            />

            <Button type="submit" className="w-full">
              {t("auth.register")}
            </Button>
          </form>
        </Form>

        <div className="text-center text-sm">
          <span className="text-muted-foreground">
            {t("auth.alreadyHaveAccount")}{" "}
          </span>
          <Link
            to={{
              pathname: "/login",
              search: location.search,
            }}
            className="text-primary hover:underline"
          >
            {t("auth.login")}
          </Link>
        </div>
      </motion.div>
      <ReCAPTCHA
        sitekey={googleRecaptchaSiteKey}
        size="invisible"
        ref={reCaptchaRef}
      />
    </div>
  );
}
